
import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator";
import Draggable from "./utilities/draggable.vue";
import { EventRecord, EventRecordDetails, EventRecordExtraDetails } from "@/store/site-monitor/types";
import { truncateString } from "@/filters";
import { Getter, namespace } from "vuex-class";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldValues } from "@/store/custom-fields/types";
import customFieldsApi from "@/services/api.customField.service";
import CustomDefinedFieldsDisplay from "./custom-fields/CustomDefinedFieldsDisplay.vue";
import { eventRecordMixins } from "@/mixins/EventRecordMixins";
import BadgeActivity from "@/components/BadgeActivity.vue";
import { ResponseBadgeActivityDetails } from "@/store/badge-activity/types";
import { UserPermissions } from "@/store/types";
import BadgeActivityApi from "@/services/api.badgeActivity.service";

const CustomFields = namespace("customFields");
const getParsedExtraDetails = eventRecordMixins.methods.getParsedExtraDetails;

@Component({
	components: {
		draggable: Draggable,
		"custom-defined-fields-display": CustomDefinedFieldsDisplay,
		"badge-activity": BadgeActivity,
	},
	filters: { truncateString },
})
export default class EventRecordDetailsPopout extends Vue {
	@Prop({ type: Boolean, default: false }) isModalShown: Boolean;
	@Prop({ type: Object }) eventRecord: EventRecord;
	@Prop({ type: Number, default: 75 }) maxTitleCharacters: Number;
	@Prop({ type: Number, default: 30 }) maxValueCharacters: Number;
	@Prop({ type: Boolean, default: false }) showBadgeActivityByDefault: Boolean;

	@CustomFields.Action retrieveCustomFields: ({
		tableType,
		live,
	}: {
		tableType: number;
		live: boolean;
	}) => Promise<void>;
	@CustomFields.State alarmCustomFields: CustomFieldDto[];

	@Getter("getPermissions") permissions: UserPermissions;

	private customFieldAlarmTableType = CustomFieldTableTypes.Alarm;
	private customFieldObjectValues: CustomFieldValues[] = [];
	private eventRecordExtraDetailObjects: EventRecordExtraDetails;
	private isLoading: boolean = false;
	private isDraggable: boolean = true;
	private badgeActivitySupported: ResponseBadgeActivityDetails | null = null;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Watch("eventRecord.EventRecordDetails.objectId")
	private async retrieveExtraData(): Promise<void> {
		this.isLoading = true;
		await this.retrieveCustomFieldValues();
		await this.checkBadgeActivitySupport();
		this.isLoading = false;
	}

	@Watch("eventRecord.EventRecordDetails.extraDetails")
	private parseExtraDetailsValue(): void {
		this.parseAndSetExtraDetails();
	}

	@Emit("disableDetailsPopout")
	private disableDetailsPopout(): boolean {
		return false;
	}

	public async mounted(): Promise<void> {
		this.parseAndSetExtraDetails();
	}

	private parseAndSetExtraDetails(): void {
		if (!this.eventRecord || !this.eventRecord.EventRecordDetails) {
			return;
		}

		const extraDetails = this.eventRecord.EventRecordDetails.extraDetails;

		if (extraDetails) {
			this.eventRecordExtraDetailObjects = getParsedExtraDetails(extraDetails);
		}
	}

	private get customFieldsEmpty(): string {
		return !this.customFieldObjectValues || this.customFieldObjectValues.length == 0 ? "custom-fields-empty" : "";
	}

	private get getEventRecordDetails(): EventRecordDetails {
		if (this.eventRecord?.EventRecordDetails) {
			return this.eventRecord.EventRecordDetails;
		} else {
			return null;
		}
	}

	private get extraDetailsEmpty(): boolean {
		return !this.eventRecordExtraDetailObjects || (this.eventRecordExtraDetailObjects.data && this.eventRecordExtraDetailObjects.data.length == 0);
	}

	private get isExtraDetailsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor","ExpandableEventDetails","ExtraDetails"]);
	}

	private get isAlarmTitleInEventRecordDetailsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor","ExpandableEventDetails","IncludeAlarmTitle"]);
	}

	private get isBadgeActivityEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "BadgeActivity"]) &&
			!!this.permissions.canViewBadgeActivity;
	}

	private get isBadgeActivitySupported(): boolean {
		return this.badgeActivitySupported &&
			this.badgeActivitySupported?.canBadgeActivity
	}

	private async checkBadgeActivitySupport(): Promise<void> {
		if (this.isBadgeActivityEnabled) {
			this.badgeActivitySupported = await BadgeActivityApi.badgeActivitySupported(this.eventRecord.EventRecordDetails.objectId);
		}
	}

	private get isCustomFieldsAlarmsEnabled(): boolean {
		return this.getFeature(["CustomFields", "Alarms"]);
	}

	private async retrieveCustomFieldValues(): Promise<void> {
		if (this.eventRecord?.EventRecordDetails?.objectId && this.isCustomFieldsAlarmsEnabled) {
			try {
				this.customFieldObjectValues = await customFieldsApi.retrieveCustomFieldsForObject(
					this.eventRecord.EventRecordDetails.objectId,
					CustomFieldTableTypes.Alarm
				);
			} catch {
				console.log(
					"Failed to get Custom Fields for object with objectId: " + this.eventRecord?.EventRecordDetails?.objectId
				);
				this.isLoading = false;
				this.customFieldObjectValues = [];
			}
		} else {
			this.customFieldObjectValues = [];
		}
	}

	private onFocusChanged(isFocused: boolean): void {
		this.isDraggable = !isFocused;
	}
}
